import React, { useCallback, useMemo, useRef, useState } from 'react'
import { DividerWrapper } from 'src/components/Analysis/components/SettingLayout'
import { getProject, updateProject } from 'src/redux/slices/project/endpoints'
import { FieldConfig, FieldConfigUnion } from 'src/redux/slices/project/types'
import { Project } from 'src/types/database-model'
import { TextFieldOnChangeEvent } from 'src/types/event-handlers'

import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'

type SurveyTextFieldProps = {
  field: keyof Project
  config: FieldConfig
  isDrawer?: boolean
}

type FieldInfo = {
  fieldConfig: FieldConfigUnion
  mode: 'numeric' | 'boolean' | 'string' | 'multiselect'
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const SurveyTextField: React.FC<SurveyTextFieldProps> = ({ field, config, isDrawer }) => {
  const { data: project, isLoading } = getProject.useQuery('')
  const { fieldConfig, mode }: FieldInfo = useMemo(() => {
    const { numericConfig, multiselectConfig, booleanConfig, stringConfig } = config
    if (numericConfig) return { fieldConfig: numericConfig, mode: 'numeric' }
    if (booleanConfig) return { fieldConfig: booleanConfig, mode: 'boolean' }
    if (stringConfig) return { fieldConfig: stringConfig, mode: 'string' }
    if (multiselectConfig) return { fieldConfig: multiselectConfig, mode: 'multiselect' }
    return {
      fieldConfig: { label: 'Field', helperText: 'Helpertext' },
      mode: 'string',
    }
  }, [config])
  const { label, helperText } = fieldConfig
  const [triggerUpdateProject, { isLoading: isUpdatingProject }] = updateProject.useMutation({
    fixedCacheKey: `update-project-${field}`,
  })

  const dbValue = useMemo(() => {
    if (!project) return null
    return project[field]
  }, [project, field])

  const [value, setValue] = useState(dbValue ?? null)
  const ref = useRef<any>()

  const [updateTimeout, setUpdateTimeout] = useState<NodeJS.Timeout | null>(null)

  const handleUpdate = useCallback(
    (val: string | boolean | number | string[]) => {
      setValue(val)
      if (!project) return
      const newUpdateTimeout = setTimeout(() => {
        triggerUpdateProject({
          [field]: val,
          id: project.id,
        })
        if (ref?.current?.blur) ref.current.blur()
      }, 1000)
      setUpdateTimeout((prev) => {
        if (prev) {
          clearTimeout(prev)
        }
        return newUpdateTimeout
      })
    },
    [setValue, setTimeout, field, project, triggerUpdateProject, ref],
  )
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      let val = e.target.value as string[]
      if (val.length === 0) val = ['none']
      if (val.length > 1) {
        if (val[val.length - 1] === 'none') {
          val = val.filter((x) => x === 'none')
        } else {
          val = val.filter((x) => x !== 'none')
        }
      }
      handleUpdate(val)
    },
    [handleUpdate],
  )
  const handleTextFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let val: string | boolean | number | string[] = ''
      if (mode === 'string') val = String(e.target.value)
      // TODO: change to checked
      if (mode === 'boolean') val = Boolean(e.target.checked)
      if (mode === 'numeric') val = Number(e.target.value)
      handleUpdate(val)
    },
    [mode, handleUpdate],
  )

  const update = useCallback(() => {
    if (!project) return
    triggerUpdateProject({
      [field]: value,
      id: project.id,
    })
    if (ref?.current?.blur) ref.current.blur()
  }, [project, field, value, ref])

  const handleEnter = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        update()
        setUpdateTimeout((prev) => {
          if (prev) clearTimeout(prev)
          return null
        })
      }
    },
    [update, setUpdateTimeout],
  )

  return (
    <>
      {fieldConfig.miniHeader && (
        <Grid item container alignItems='center'>
          <DividerWrapper>
            <Typography variant='overline' style={{ margin: '0 0.5rem' }}>
              {fieldConfig.miniHeader}
            </Typography>
          </DividerWrapper>
        </Grid>
      )}
      <Grid item>
        {mode === 'numeric' && (
          <TextField
            inputRef={ref}
            label={label}
            helperText={helperText}
            value={value ?? ''}
            onChange={handleTextFieldChange}
            onKeyDown={handleEnter}
            variant='outlined'
            size='small'
            type='number'
            // defaultValue={fieldConfig.default ?? ''}
            InputProps={{
              inputProps: {
                min: fieldConfig?.min,
                max: fieldConfig?.max,
                steps: fieldConfig?.step,
              },
            }}
          />
        )}
        {mode === 'string' && (
          <TextField
            inputRef={ref}
            label={label}
            helperText={helperText}
            value={value ?? ''}
            onChange={handleTextFieldChange}
            onKeyDown={handleEnter}
            variant='outlined'
            size='small'
          />
        )}

        {mode === 'multiselect' && (
          <FormControl fullWidth style={{ width: '100%' }}>
            <InputLabel id={`multiselect-inputlabel-${field}`}>{label}</InputLabel>
            <Select
              labelId={`multiselect-inputlabel-${field}`}
              id={`multiselect-field-${field}`}
              multiple
              value={value ?? ['none']}
              onChange={handleSelectChange}
              input={<Input />}
              MenuProps={MenuProps}
            >
              {fieldConfig.options?.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText id={`multiselect-helper-${field}`}>{helperText}</FormHelperText>
          </FormControl>
        )}

        {mode === 'boolean' && (
          <FormControlLabel
            control={
              <Switch
                checked={value ? Boolean(value) : false}
                onChange={handleTextFieldChange}
                name={`field-${field}-checked`}
                color='primary'
              />
            }
            label={fieldConfig.label}
          />
        )}
      </Grid>
    </>
  )
}
export default SurveyTextField
